import React, { useState } from 'react';
import Stack from 'src/app/atoms/Layout/Stack/Stack';
import { FooterFormContainer, FooterHubspotForm } from './FooterForm.styles';
import Typography from 'src/app/atoms/Typography/Typography';
import { submitHubSpotFormBuilder } from 'src/functions/hubspot/hubspot.service';
import { MainLayout } from 'molecules/Header/Header/Header';
import { useFormResultController } from 'src/hooks/useFormResultController';
import Button from 'src/app/atoms/Button/Button';
import useHubspotFormCreation from 'src/hooks/useHubspotFormCreation';

interface FooterFormProps {
  type: MainLayout;
}

const submitHubSpotForm = submitHubSpotFormBuilder('#hubspotFooterForm');

const FooterForm = ({ type }: FooterFormProps): JSX.Element => {
  const [wasSubmitted, setWasSubmitted] = useState(false);
  const [buttonIsBlocked, setButtonIsBlocked] = useState(false);
  const [formFailure, setFormFailure] = useState(false);

  const { isSubmitted, setFormSubmission } = useFormResultController();

  useHubspotFormCreation({
    formId: type === 'pro' ? 'b5c092d9-163f-47b1-9fc3-7a5b6c953a64' : '79e916cb-d4f8-4ea5-990c-ee51c8259e7b',
    formWrapperId: 'hubspotFooterForm',
    dependencies: [isSubmitted],
    blockDependencies: [isSubmitted],
    onFormReadyCallback: () => null,
    onFormSubmitCallback: () => {
      setButtonIsBlocked(true);
    },
    onFormSubmittedCallback: () => {
      setWasSubmitted(true);
      setFormSubmission();
    },
    errorCallback: () => {
      setTimeout(() => {
        setFormFailure(true);
        setButtonIsBlocked(true);
      }, 2000);
    },
  });

  const showThankYou = wasSubmitted || isSubmitted;

  const renderFormFailure = () => (
    <Stack
      justify='flex-start'
      align='flex-start'
      margin='spacing-auto'
    >
      <Typography
        type='heading'
        size='xs'
        color='text-primary-on-brand'
      >
        Something went wrong, please try again later.
      </Typography>
    </Stack>
  );

  const renderThankYou = () => (
    <Stack
      direction='column'
      gap='spacing-xl'
      justify='flex-start'
      align='flex-start'
      margin={['spacing-auto', 'spacing-auto', 'spacing-auto', 'spacing-none']}
    >
      <Typography
        type='heading'
        size='xs'
        color='text-primary-on-brand'
      >
        Thank you!
      </Typography>
      <Typography
        type='paragraph'
        size='md'
        color='text-brand-secondary'
      >
        Keep an eye on your inbox.
      </Typography>
    </Stack>
  );

  const renderForm = () => (
    <>
      <Stack
        direction='column'
        gap='spacing-xs'
        justify='flex-start'
        align='flex-start'
      >
        <Typography
          type='heading'
          size='xs'
          color='text-primary-on-brand'
        >
          Build a $1 Million Community
        </Typography>
        <Typography
          type='paragraph'
          size='md'
          color='text-tertiary'
        >
          This free masterclass went viral—sign up to learn why.
        </Typography>
      </Stack>
      <Stack
        direction='column'
        gap='spacing-xl'
        justify='flex-start'
        align='stretch'
        dataColorMode='light'
      >
        <FooterHubspotForm id='hubspotFooterForm' />
        <Button
          size='lg'
          variant='primary'
          onClick={submitHubSpotForm}
          disabled={buttonIsBlocked}
        >
          Submit
        </Button>
      </Stack>
    </>
  );

  const handleFormStateRender = () => {
    if (formFailure) return renderFormFailure();
    if (showThankYou) return renderThankYou();

    return renderForm();
  };

  return (
    <Stack
      columns={5}
      direction='row'
      justify='flex-end'
      align='flex-start'
      gap='spacing-4xl'
      dataColorMode='dark'
      padding={['spacing-none', 'spacing-none', 'spacing-none', 'spacing-4xl']}
      paddingLg='spacing-none'
    >
      <FooterFormContainer
        direction='column'
        background='bg-secondary'
        borderRadius='radius-2xl'
        gap='spacing-3xl'
        padding='spacing-4xl'
      >
        {handleFormStateRender()}
      </FooterFormContainer>
    </Stack>
  );
};

export default FooterForm;
